import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AlertController, IonicModule, NavController, PopoverController } from '@ionic/angular';
import _ from 'lodash';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { CustomFieldsModule } from 'src/app/components/custom-fields/custom-fields.module';
import { EmojiPickerComponent } from 'src/app/components/emoji-picker/emoji-picker.component';
import { IntakeSettingsComponent } from 'src/app/components/intake-settings/intake-settings.component';
import { SlackTextEditorComponent } from 'src/app/components/slack-text-editor/slack-text-editor.component';
import {
  TicketIntakeConfig,
  TicketIntakeVisualizerComponent,
} from 'src/app/components/ticket-intake-visualizer/ticket-intake-visualizer.component';
import {
  BOT_HANDLING,
  CONVERSATION_PRIORITY_VALUES,
  DEFAULT_PRIORITY_LABEL_MAP_FORM,
  DEFAULT_PRIORITY_TOKENS,
  FORM_PLACEHOLDER_DESCRIPTIONS,
  PROJECT_MEMBER_ROLES,
  REPLY_TIMEOUT_MINUTES_DEFAULT,
  routes,
  SUPPORT_STEP_KEYWORDS,
} from 'src/app/constants';
import {
  fillPriorityLabelMapFormWithCurrentValues,
  genericCompareFn,
  getIconForConversationPriority,
  isUrl,
  processPriorityLabelMap,
} from 'src/app/utils/utils';
import {
  AGENT_HANDOFF_MODES,
  CustomerViewSettings,
  ExternalChatConnection,
  InboundEmailRule,
  Organizer,
  PriorityTokens,
  SlackChannel,
  Widget,
} from 'src/models';
import {
  AGENT_HANDOFF_MODE_LABELS,
  CHANNEL_TYPES,
  INTAKE_MODE_LABELS,
  INTAKE_MODES,
  IntakeSettings,
} from 'src/models/channels';
import { Project } from 'src/models/projects';
import { ColonToEmojiPipe } from 'src/pipes/colonToEmoji.pipe';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { EmojisService } from 'src/services/emojis.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxTippyModule,
    CustomFieldsModule,
    ChannelSelectorModule,
    RouterModule,
    SlackTextEditorComponent,
    IntakeSettingsComponent,
    TicketIntakeVisualizerComponent,
    SharedPipesModule,
    ColonToEmojiPipe,
  ],
  selector: 'app-channel-admin-settings',
  templateUrl: './channel-admin-settings.page.html',
  styleUrls: ['./channel-admin-settings.page.scss'],
})
export class ChannelAdminSettingsPage implements OnInit {
  customer: Partial<Organizer>;
  customerId: string;
  project: Partial<Project>;
  isLoading = false;
  slackChannelId = '';
  slackChannel?: SlackChannel;
  slackChannelName = '';
  slackChannelIsPrivate: boolean | null = null;
  slackChannelIsShared: boolean | null = null;
  slackTeamId = '';
  syncSlackChannel = false;
  channel: any;
  supportSteps: any[] = [];
  replyTimeoutMinutes: number;
  showAdvanced = false;
  overrideTriage: boolean;
  defaultTriageChannelId: string;
  triageChannel?: SlackChannel;
  enableAutomatedTicketing: boolean;
  enableAgentAutomatedTicketing: boolean;
  createdBy: string;
  botHandling?: string;
  autoresponder?: any;
  enabledAutomatedTicketing: boolean | null = null;
  assignToTaggedUserEnabled: boolean | null = null;
  assignToFirstResponderEnabled: boolean | null = null;
  isInternal: boolean;
  supportStepRulesets: any[] = [];
  supportStepRulesetId?: string;
  customerViewEnabled = null;
  customerViewSettings?: Partial<CustomerViewSettings>;
  conversationCreationConditions: any[] = [];
  conditionalConversationCreationEnabled = false;
  lookups: Record<string, Record<string, string>> = {};
  inboundEmailRule?: Partial<InboundEmailRule>;
  externalChatConnection?: Partial<ExternalChatConnection>;
  widget?: Partial<Widget>;
  blockedEmailField: string;
  type?: string;
  overrideDefaultCustomerViewMessage = false;
  intakeSettings: IntakeSettings;
  overrideIntakeSettings = false;
  customTokens: Record<string, any> = {};
  ticketIntakeConfig: TicketIntakeConfig;
  priorityLabelMapForm: Record<string, PriorityTokens> = _.cloneDeep(DEFAULT_PRIORITY_LABEL_MAP_FORM);
  defaultPriorityLabelMap = DEFAULT_PRIORITY_TOKENS;

  SUPPORT_STEP_KEYWORDS = SUPPORT_STEP_KEYWORDS;
  INTAKE_MODES = INTAKE_MODES;
  INTAKE_MODE_LABELS = INTAKE_MODE_LABELS;
  CHANNEL_TYPES = CHANNEL_TYPES;
  AGENT_HANDOFF_MODES = AGENT_HANDOFF_MODES;
  AGENT_HANDOFF_MODE_LABELS = AGENT_HANDOFF_MODE_LABELS;

  CONDITION_TYPES = {
    CONTAINS: 'contains',
    NOT_CONTAINS: 'notContains',
    EQUALS: '==',
    NOT_EQUALS: '!=',
    LIKE: 'like',
  };

  CONDITION_TYPE_LABELS = {
    [this.CONDITION_TYPES.CONTAINS]: 'contains',
    [this.CONDITION_TYPES.NOT_CONTAINS]: 'does not contain',
    [this.CONDITION_TYPES.EQUALS]: 'equals',
    [this.CONDITION_TYPES.NOT_EQUALS]: 'does not equal',
    [this.CONDITION_TYPES.LIKE]: 'contains',
  };

  CONDITIONS = {
    text: {
      label: 'Initial message text',
      types: [this.CONDITION_TYPES.LIKE],
    },
  };

  CONVERSATION_PRIORITY_VALUES = CONVERSATION_PRIORITY_VALUES;
  FORM_PLACEHOLDER_DESCRIPTIONS = FORM_PLACEHOLDER_DESCRIPTIONS;
  public isURL = isUrl;

  constructor(
    router: Router,
    public authService: AuthService,
    private navController: NavController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private slackService: SlackService,
    private customersService: CustomersService,
    private apiService: ApiService,
    private projectsService: ProjectsService,
    private searchablePopoverService: SearchablePopoverService,
    private popoverCtrl: PopoverController,
    private emojisService: EmojisService,
  ) {
    this.customerId = router.url.split('/')[3];
  }

  async ngOnInit() {
    await this.loadSupportStepRulesets();
    await this.emojisService.loadEmojis();
    await this.getCustomer();
  }

  async loadSupportStepRulesets() {
    const supportStepRulesets: any = await this.apiService.postPromise(`/support-step-rulesets/list`, {});

    this.supportStepRulesets = supportStepRulesets.data;
  }

  async getCustomer() {
    const customer: Partial<Organizer> = await this.customersService.get(this.customerId);

    const user = this.authService.user;

    this.project = await this.projectsService.getById(customer?.projectId);

    const projectMember = this.authService.projects.find((p) => p.id === customer?.projectId);

    if (
      !user ||
      ((customer.adminIds ?? []).indexOf(this.authService.userId) < 0 &&
        !this.authService.isTenantAdmin &&
        projectMember?.role !== PROJECT_MEMBER_ROLES.OWNER)
    ) {
      this.navController.navigateRoot(`/${routes.DASHBOARD}/home`);

      return;
    }

    this.slackChannelName = customer.slackChannel?.name;
    this.slackChannelId = customer.slackChannel?.id;
    this.slackChannelIsPrivate = customer.slackChannel?.isPrivate;
    this.slackChannelIsShared = customer.slackChannel?.isShared;
    this.slackTeamId = customer.slackChannel?.teamId;

    this.customTokens = _.cloneDeep(customer.customTokens || {});

    const currentPriorityMap = _.get(this.customTokens, 'PRIORITY_LABEL_MAP', {}) as Record<
      string,
      Partial<PriorityTokens> | null
    >;
    this.priorityLabelMapForm = fillPriorityLabelMapFormWithCurrentValues(currentPriorityMap);

    this.syncSlackChannel = customer.syncSlackChannel ?? false;

    this.createdBy = customer.createdBy;
    this.supportStepRulesetId = customer.supportSteps?.length
      ? 'custom'
      : customer.supportStepRuleset
        ? customer.supportStepRuleset?.id
        : 'default';
    this.supportSteps = customer.supportSteps?.length
      ? customer.supportSteps
      : customer.supportStepRuleset
        ? customer.supportStepRuleset.rules
        : this.project.supportSteps;
    this.replyTimeoutMinutes = customer.replyTimeoutMinutes;
    this.defaultTriageChannelId = customer.defaultTriageChannelId;
    this.overrideTriage = !!customer.defaultTriageChannelId;
    this.enableAutomatedTicketing =
      customer.disableAutomatedTicketing == null ? null : !customer.disableAutomatedTicketing;
    this.enableAgentAutomatedTicketing = customer.enableAgentAutomatedTicketing ?? null;
    this.botHandling = customer.botHandling;
    this.intakeSettings = customer.intakeSettings;
    this.customerViewEnabled = customer.customerViewEnabled;
    this.customerViewSettings = { ...(customer.customerViewSettings || {}) };
    this.autoresponder = customer.autoresponder;
    this.assignToTaggedUserEnabled = customer.assignToTaggedUserEnabled;
    this.assignToFirstResponderEnabled = customer.assignToFirstResponderEnabled;
    this.isInternal = customer.isInternal;
    this.conversationCreationConditions = customer.conversationCreationConditions ?? [];
    this.conditionalConversationCreationEnabled = customer.conversationCreationConditions.length > 0;
    this.inboundEmailRule = customer.inboundEmailRule;
    this.externalChatConnection = customer.externalChatConnection;
    this.widget = customer.widget;
    this.type = customer.type;
    this.overrideDefaultCustomerViewMessage =
      this.customTokens['CUSTOMER_VIEW_CUSTOM_MESSAGE'] !== null &&
      this.customTokens['CUSTOMER_VIEW_CUSTOM_MESSAGE'] !== undefined;
    this.overrideIntakeSettings = !!customer.intakeSettings;

    // Initialize ticket intake visualizer config
    this.initializeTicketIntakeConfig();

    this.customer = customer;

    if (this.slackChannelId) {
      this.loadSlackChannel();
    }

    if (this.defaultTriageChannelId) {
      this.loadTriageChannel();
    }

    if (this.conversationCreationConditions.length) {
      await this.loadLookups();
    }
  }

  async loadSlackChannel() {
    const slackChannel = await this.slackService.getChannel(this.slackChannelId, this.slackTeamId);

    if (slackChannel) {
      this.slackChannel = slackChannel;
    }
  }

  async loadTriageChannel() {
    const triageChannel = await this.slackService.getChannel(this.defaultTriageChannelId);

    if (triageChannel) {
      this.triageChannel = triageChannel;
    }
  }

  async save() {
    const user = this.authService.user;

    if (!user) {
      this.navController.navigateRoot('login');
      return;
    }

    if (!this.customer.name) {
      this.msgSrvc.show('Please add a customer name');
      return;
    }

    this.isLoading = true;

    if (!this.customTokens) {
      this.customTokens = {};
    }
    this.customTokens.PRIORITY_LABEL_MAP = processPriorityLabelMap(this.priorityLabelMapForm);

    const updates: Partial<Organizer> = {
      slackChannelId: this.customer.slackChannelId,
    };

    if (this.customer.slackChannelId !== this.slackChannelId && this.slackChannel) {
      updates.slackChannelId = this.slackChannel?.id ?? null;
    }

    if (this.customer.slackTeamId !== this.slackTeamId && this.slackChannel) {
      updates.slackTeamId = this.slackChannel?.teamId ?? null;
    }

    updates.supportSteps = this.supportStepRulesetId === 'custom' ? this.supportSteps : null;
    updates.supportStepRulesetId =
      this.supportStepRulesetId === 'custom' || this.supportStepRulesetId === 'default'
        ? null
        : this.supportStepRulesetId;
    updates.replyTimeoutMinutes = this.replyTimeoutMinutes;
    updates.defaultTriageChannelId = this.overrideTriage ? this.defaultTriageChannelId : null;
    updates.disableAutomatedTicketing = this.enableAutomatedTicketing == null ? null : !this.enableAutomatedTicketing;
    updates.enableAgentAutomatedTicketing = this.enableAgentAutomatedTicketing;
    updates.intakeSettings = this.overrideIntakeSettings ? this.intakeSettings : null;
    updates.botHandling = this.botHandling;
    updates.customerViewEnabled = this.customerViewEnabled;
    updates.customerViewSettings = this.customerViewSettings;
    updates.autoresponder = this.autoresponder;
    updates.assignToTaggedUserEnabled = this.assignToTaggedUserEnabled;
    updates.assignToFirstResponderEnabled = this.assignToFirstResponderEnabled;
    updates.conversationCreationConditions = this.conditionalConversationCreationEnabled
      ? this.conversationCreationConditions
      : [];
    updates.inboundEmailRule = this.inboundEmailRule ?? {};
    updates.externalChatConnection = this.externalChatConnection ?? {};
    updates.widget = this.widget ?? {};
    updates.customTokens = this.customTokens;

    this.isLoading = true;

    try {
      await this.customersService.update(this.customerId, updates);

      await this.getCustomer();

      this.isLoading = false;
      this.msgSrvc.show('✔︎ Updated');
    } catch (err) {
      console.log(err);

      this.isLoading = false;
      this.msgSrvc.show('Something went wrong');
    }
  }

  async delete() {
    const subHeader = "We'll disconnect this channel, though conversations will still be visible in the dashboard";
    const alert = await this.alertCtrl.create({
      header: 'Are you sure?',
      subHeader,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Do it',
          handler: async () => {
            try {
              this.isLoading = true;

              await this.customersService.delete(this.customerId);

              this.isLoading = false;

              this.navController.navigateRoot(`${routes.DASHBOARD}/customers`);
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.showError(e);
            }
          },
        },
      ],
    });
    alert.present();
  }

  openSlack() {
    window.open(`https://slack.com/app_redirect?channel=${this.customer.slackChannelId}`);
  }

  onSelectSupportStepRulesetId(event) {
    const supportStepRulesetId = event.detail.value;

    if (!supportStepRulesetId) {
      return;
    }

    if (supportStepRulesetId === 'default') {
      this.supportStepRulesetId = supportStepRulesetId;
      this.supportSteps = this.project.supportSteps;
    } else if (supportStepRulesetId === 'custom') {
      this.supportStepRulesetId = supportStepRulesetId;
      this.supportSteps = this.customer.supportSteps ?? [];
    } else {
      const supportStepRuleset = this.supportStepRulesets.find((a) => a.id === supportStepRulesetId);

      if (supportStepRuleset) {
        this.supportStepRulesetId = supportStepRulesetId;
        this.supportSteps = supportStepRuleset.rules;
      }
    }
  }

  replyTimeoutMinutesCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  replyTimeoutMinutesDefaultLabel() {
    const tenantReplyTimeoutMinutes = this.authService.tenant.replyTimeoutMinutes ?? REPLY_TIMEOUT_MINUTES_DEFAULT;

    if (tenantReplyTimeoutMinutes === 0) {
      return 'Never';
    }

    if (tenantReplyTimeoutMinutes === -1) {
      return 'Always';
    }

    if (tenantReplyTimeoutMinutes < 60) {
      return `${tenantReplyTimeoutMinutes} minute${tenantReplyTimeoutMinutes === 1 ? '' : 's'}`;
    }

    const tenantReplyTimeoutHours = tenantReplyTimeoutMinutes / 60;

    return `${tenantReplyTimeoutHours} hour${tenantReplyTimeoutHours === 1 ? '' : 's'}`;
  }

  enableAutomatedTicketingCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  isInteractiveModeCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  enableAutomatedTicketingDefaultLabel() {
    const disableAutomatedTicketing = this.authService.tenant.disableAutomatedTicketing ?? false;

    if (disableAutomatedTicketing) {
      return 'Disabled';
    }

    return 'Enabled';
  }

  intakeSettingsDefaultLabel() {
    return INTAKE_MODE_LABELS[
      this.authService.tenant?.defaultChannelIntakeSettings?.form?.enabled
        ? INTAKE_MODES.FORM
        : this.authService.tenant?.defaultChannelIntakeSettings?.bot?.enabled
          ? INTAKE_MODES.BOT_TRIAGE
          : INTAKE_MODES.IMMEDIATE
    ];
  }

  genericCompareFn(v1, v2) {
    return genericCompareFn(v1, v2);
  }

  customerViewEnabledDefaultLabel() {
    const customerViewEnabled = this.authService.tenant.customerViewEnabled ?? false;

    if (!customerViewEnabled) {
      return 'Disabled';
    }

    return 'Enabled';
  }

  isCustomerViewPrivateDefaultLabel() {
    const isCustomerViewPrivate = this.authService.tenant.customerViewSettings?.isPrivate ?? false;

    return !isCustomerViewPrivate ? 'Disabled' : 'Enabled';
  }

  isCustomerViewPinnedToBottomDefaultLabel() {
    const isCustomerViewPinnedToBottom = this.authService.tenant.customerViewSettings?.isPinnedToBottom ?? false;

    return !isCustomerViewPinnedToBottom ? 'Disabled' : 'Enabled';
  }

  botHandlingCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  botHandlingDefaultLabel() {
    const tenantBotHandling = this.authService.tenant.botHandling ?? BOT_HANDLING.OFF;

    if (tenantBotHandling === BOT_HANDLING.OFF) {
      return 'Disabled';
    }

    return 'Enabled';
  }

  assignToTaggedUserEnabledCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  assignToTaggedUserEnabledDefaultLabel() {
    const tenantAssignToTaggedUserEnabled = this.authService.tenant.assignToTaggedUserEnabled ?? true;

    if (tenantAssignToTaggedUserEnabled === false) {
      return 'Disabled';
    }

    return 'Enabled';
  }

  assignToFirstResponderEnabledCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  assignToFirstResponderEnabledDefaultLabel() {
    const tenantAssignToFirstResponderEnabled = this.authService.tenant.assignToFirstResponderEnabled ?? true;

    if (tenantAssignToFirstResponderEnabled === false) {
      return 'Disabled';
    }

    return 'Enabled';
  }

  onSlackChannelChanged(slackChannel?: SlackChannel) {
    this.slackChannel = slackChannel;

    this.customer.slackChannelId = slackChannel?.id ?? null;
    this.slackChannelId = slackChannel?.id ?? null;
    this.slackChannelName = slackChannel?.name ?? null;
    this.slackChannelIsPrivate = slackChannel?.isPrivate ?? false;
    this.slackChannelIsShared = slackChannel?.isShared ?? false;
    this.slackTeamId = slackChannel?.teamId ?? null;

    if (slackChannel) {
      this.isInternal = !slackChannel.isShared;
    }

    this.save();
  }

  onTriageChannelChanged(slackChannel?: SlackChannel) {
    this.defaultTriageChannelId = slackChannel?.id ?? null;
    this.triageChannel = slackChannel;
  }

  supportStepsTrackBy(_i: number, supportStepRuleset: any) {
    return supportStepRuleset.id;
  }

  autoresponderDidChange(autoresponder: any) {
    this.autoresponder = autoresponder;
  }

  removeConditionalRule(i: number) {
    this.conversationCreationConditions.splice(i, 1);
  }

  onConditionChange(condition: any) {}

  conditionValues() {
    return Array.from(Object.entries(this.CONDITIONS));
  }

  showSearchOptions(condition: any) {}

  addConditionalRule() {
    const conversationCreationConditions = this.conversationCreationConditions ?? [];

    conversationCreationConditions.push({
      field: 'text',
      operator: 'like',
      value: '',
    });

    this.conversationCreationConditions = conversationCreationConditions;
  }

  async loadLookups() {
    const lookupIds: Record<string, string[]> = {};

    for (const conversationCreationCondition of this.conversationCreationConditions) {
      if (!lookupIds[conversationCreationCondition.field]) {
        lookupIds[conversationCreationCondition.field] = [];
      }

      lookupIds[conversationCreationCondition.field].push(conversationCreationCondition.value);
    }

    if (Object.keys(lookupIds).length) {
      this.lookups = await this.apiService.postPromise('/saved-filters/get-lookup-values', lookupIds);
    }
  }

  onConditionalConversationCreationChanged(event: any) {
    if (event.target.value === 'on' && !this.conversationCreationConditions.length) {
      this.addConditionalRule();
    }
  }

  getAutomatedTicketingWithDefault() {
    return (
      this.enableAutomatedTicketing ??
      (this.authService.tenant.disableAutomatedTicketing != null
        ? !this.authService.tenant.disableAutomatedTicketing
        : false)
    );
  }

  showEmailHelpdesk() {
    window.open('https://docs.unthread.io/email/email-slack-connector', '_blank');
  }

  addBlockedEmail(rule: Partial<InboundEmailRule>) {
    if (rule.blocklist) {
      rule.blocklist.push(this.blockedEmailField.toLowerCase());
    } else {
      rule.blocklist = [this.blockedEmailField.toLowerCase()];
    }

    this.blockedEmailField = '';
  }

  deleteBlocklistEntry(rule: Partial<InboundEmailRule>, index: number) {
    rule.blocklist.splice(index, 1);
  }

  getForwardEmail(rule: Partial<InboundEmailRule>) {
    return `${this.authService.tenantId}+${rule.shortId}@mail.unthread.io`;
  }

  copyToClipboard(rule: Partial<InboundEmailRule>) {
    navigator.clipboard
      .writeText(this.getForwardEmail(rule))
      .then(() => {
        this.msgSrvc.show('Copied!');
      })
      .catch(() => {
        // Unable to copy
      });
  }

  onMessageContentChange(event: string | null) {
    this.customTokens['CUSTOMER_VIEW_CUSTOM_MESSAGE'] = event;
  }

  onMessageOverrideChange() {
    if (!this.overrideDefaultCustomerViewMessage) {
      this.customTokens['CUSTOMER_VIEW_CUSTOM_MESSAGE'] = null;
    }
  }

  initializeTicketIntakeConfig() {
    const defaultIntakeSettings = this.authService.tenant?.defaultChannelIntakeSettings;
    const defaultIntakeMode = defaultIntakeSettings?.form?.enabled
      ? INTAKE_MODES.FORM
      : defaultIntakeSettings?.bot?.enabled
        ? INTAKE_MODES.BOT_TRIAGE
        : INTAKE_MODES.IMMEDIATE;

    this.ticketIntakeConfig = {
      intakeSettings: this.intakeSettings || {
        bot: {
          enabled: false,
          maxResponses: null,
          handoffMode: AGENT_HANDOFF_MODES.PROMPT,
        },
        form: {
          enabled: false,
        },
      },
      supportStepRulesetId: this.supportStepRulesetId,
      automatedTicketingEnabled: this.enableAutomatedTicketing,
      autoresponder: this.autoresponder,
      overrideIntakeSettings: this.overrideIntakeSettings,
      intakeMode: this.overrideIntakeSettings
        ? this.intakeSettings?.form?.enabled
          ? INTAKE_MODES.FORM
          : this.intakeSettings?.bot?.enabled
            ? INTAKE_MODES.BOT_TRIAGE
            : INTAKE_MODES.IMMEDIATE
        : defaultIntakeMode,
      supportSteps: this.supportSteps || [],
    };
  }

  onTicketIntakeConfigChange(config: TicketIntakeConfig) {
    this.ticketIntakeConfig = config;

    // Update automated ticketing
    this.enableAutomatedTicketing = config.automatedTicketingEnabled;

    // Update SLA settings
    if (config.supportStepRulesetId) {
      this.supportStepRulesetId = config.supportStepRulesetId;
    }

    // Update support steps if they exist in the config
    if (config.supportSteps) {
      this.supportSteps = config.supportSteps;
    }

    this.overrideIntakeSettings = config.overrideIntakeSettings;
    this.intakeSettings = config.intakeSettings;
    this.autoresponder = config.autoresponder;
  }

  isCustomerViewIncludeCountDefaultLabel() {
    const isCustomerViewIncludeCount = this.authService.tenant.customerViewSettings?.includeCount ?? true;

    return isCustomerViewIncludeCount ? 'Enabled' : 'Disabled';
  }

  public getIconForPriority(priorityKey: string): string {
    const priorityValue = this.CONVERSATION_PRIORITY_VALUES[priorityKey];
    if (priorityValue === undefined) {
      return 'help-circle-outline'; // Fallback icon
    }
    return getIconForConversationPriority(priorityValue);
  }

  async openPriorityEmojiPicker(priorityKey: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL', event: any) {
    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: (emoji: any) => {
          popover.dismiss();
          let emojiToStore: string;

          if (emoji.custom) {
            emojiToStore = `:${emoji.id}:`;
          } else if (emoji.colons) {
            emojiToStore = emoji.colons;
          } else if (emoji.id) {
            emojiToStore = `:${emoji.id}:`;
          }

          if (this.priorityLabelMapForm[priorityKey]) {
            this.priorityLabelMapForm[priorityKey].EMOJI = emojiToStore;
          }
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event,
    });

    popover.present();
  }

  onModalSubtitleChange(event: string | null) {
    this.customTokens['CREATE_CONVERSATION_MODAL_SUBTITLE'] = event;
  }
}
